import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { Company, FormViewService, IntegrationMappingService, PosthogService, SubmissionService, ToastService } from '@xilo-mono/form-contracts';
import { decodeToken } from '@xilo-mono/form-helper';
import { get } from 'lodash';
import { GooglePlaceDirective } from 'ngx-google-places-autocomplete';
import { debounceTime, filter, ReplaySubject, takeUntil, tap } from 'rxjs';
import { changeInputCSS } from '../helper/form';
import { Address } from 'ngx-google-places-autocomplete/objects/address';

@Component({
  selector: 'xilo-mono-form-viewer-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss', '../../form-viewer.component.scss']
})
export class AddressComponent extends FieldType implements AfterViewInit, OnDestroy {
  @ViewChild('placesRef') placesRef: GooglePlaceDirective;
  @ViewChild('addressInput') addressInputRef: ElementRef;

  changeInputCSS = changeInputCSS;

  googlePlacesOptions = {
    bounds: null,
    types: [],
    componentRestrictions: {
      country: 'US'
    },
    fields: [],
    strictBounds: false,
    origin: null
  };
  errorMessage = '';
  addressFieldText = 'Edit Address - Add Suite/Apt/Unit/etc.';
  hasError = false;
  addressSelected = false;
  streetAddress;
  city;
  state;
  zipCode;
  formInitialized = false;
  addressIsShowing = false;
  originalAddress: FormlyFieldConfig;
  company: Company;
  private _lastValidatedAddress: string;
  get token() {
    return localStorage.getItem('token');
  }

  constructor(
    public formViewService: FormViewService,
    private cdr: ChangeDetectorRef,
    private integrationMappingService: IntegrationMappingService,
    private submissionService: SubmissionService,
    private toastService: ToastService,
    private posthogService: PosthogService,
    private renderer: Renderer2
  ) {
    super();
  }

  private readonly destroyed$ = new ReplaySubject<boolean>(1);
  private keydownListener?: () => void;

  ngAfterViewInit(): void {
    this.setPlacesOptions();
    this.attachLastAddressOption();
    this.setupKeyListener();

    /**
     * There is a race condition occurring where the submission record
     * has not yet been fetched at the point that valueChanges() is firing,
     * and onUpdate() passes blank metadata values to the backend during
     * update() of the submission
     */

    // We set this for newly created repeat section items
    if (this.model[this.field.key as string] !== undefined) {
      this.geocodeAddressString(this.model[this.field.key as string]);
    }

    // Add valueChanges listener with debounce to validate address
    this.formControl.valueChanges
      .pipe(
        debounceTime(5000),
        tap((value) => {
          // Only validate if not already selected from dropdown
          this._lastValidatedAddress = this.formControl.value;
          if (!this.addressSelected && !this.formControl.touched && this.formControl.value) {
            this.checkForValidAddress(false);
          }
          this.geocodeAddressString(value);
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();

    if (this.keydownListener) {
      this.keydownListener();
    }
  }

  private setupKeyListener(): void {
    setTimeout(() => {
      if (this.addressInputRef && this.addressInputRef.nativeElement) {
        this.keydownListener = this.renderer.listen(
          this.addressInputRef.nativeElement,
          'keydown',
          (event: KeyboardEvent) => {
            const isCmdEnter = (event.key === 'Enter' && (event.metaKey || event.ctrlKey));
            const isShiftEnter = (event.key === 'Enter' && event.shiftKey);

            if (isShiftEnter || isCmdEnter) {
              event.preventDefault();
              event.stopPropagation();
              const customEvent = new CustomEvent('addressNavigationKeyPressed', {
                bubbles: true,
                detail: { key: event.key, modifiers: { shift: event.shiftKey, meta: event.metaKey, ctrl: event.ctrlKey } }
              });
              this.addressInputRef.nativeElement.dispatchEvent(customEvent);
              this.addressInputRef.nativeElement.blur();
            }
          }
        );
      }
    }, 100);
  }

  touched() {
    this.errorMessage = null;
    this.formControl.markAllAsTouched();

    if ((this.formControl.value === '' || this.formControl.value === null) && this.formControl.hasError('required')) {
      this.errorMessage = 'This field is required';
      return;
    }

    // If we already have a selected address from the dropdown, no need to validate
    if (this.addressSelected) {
      return;
    }

    // Only validate if there's a value and the field has been touched
    if (this.formControl.value && this.formControl.touched) {
      // Use setTimeout to ensure addressSelected flag has time to be processed
      // from any potential dropdown selection
      setTimeout(() => {
        // If address was selected during the timeout, skip validation
        if (this.addressSelected) {
          return;
        }

        this.checkForValidAddress(true);
      }, 1000); // 1000ms delay to allow for addressSelected to be set
    }
  }

  checkForValidAddress(runDataPulls = true) {
    // Store the current value to check if it has changed
    const currentValue = this.formControl.value;

    // Check if we've already validated this exact address
    if (this._lastValidatedAddress === currentValue) {
      return; // Skip validation if the address hasn't changed
    }

    // Update the last validated address
    this._lastValidatedAddress = currentValue;

    // First, do a basic validation check for state and zip pattern
    const addressString = currentValue.toString().toLowerCase();

    // Check if the address string contains a state code or name and a zip code pattern
    const statePattern =
      /\b(al|ak|az|ar|ca|co|ct|de|fl|ga|hi|id|il|in|ia|ks|ky|la|me|md|ma|mi|mn|ms|mo|mt|ne|nv|nh|nj|nm|ny|nc|nd|oh|ok|or|pa|ri|sc|sd|tn|tx|ut|vt|va|wa|wv|wi|wy|alabama|alaska|arizona|arkansas|california|colorado|connecticut|delaware|florida|georgia|hawaii|idaho|illinois|indiana|iowa|kansas|kentucky|louisiana|maine|maryland|massachusetts|michigan|minnesota|mississippi|missouri|montana|nebraska|nevada|new hampshire|new jersey|new mexico|new york|north carolina|north dakota|ohio|oklahoma|oregon|pennsylvania|rhode island|south carolina|south dakota|tennessee|texas|utah|vermont|virginia|washington|west virginia|wisconsin|wyoming)\b/i;
    const zipPattern = /\b\d{5}(-\d{4})?\b/;

    const hasStatePattern = statePattern.test(addressString);
    const hasZipPattern = zipPattern.test(addressString);

    if (!hasStatePattern || !hasZipPattern) {
      // If basic pattern check fails, don't even try geocoding
      this.hasError = true;
      this.formControl.setErrors({ incompleteAddress: true });
      this.errorMessage = 'Please enter a complete address with state and zip code';
      this.cdr.detectChanges();
      return;
    }

    // If basic validation passes, proceed with geocoding for more accurate validation
    const geocoder = new google.maps.Geocoder();

    const geocoderRequest: google.maps.GeocoderRequest = {
      address: currentValue,
      region: 'us',
      componentRestrictions: { country: 'US' }
    };

    geocoder.geocode(geocoderRequest, (results: google.maps.GeocoderResult[], status) => {
      if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
        // Valid address found, check if it has all required components
        const result = results[0];
        const addressComponents = result.address_components || [];

        // Check if address has state and zip code in the components
        const hasState = addressComponents.some(comp => comp.types.includes('administrative_area_level_1'));
        const hasZip = addressComponents.some(comp => comp.types.includes('postal_code'));

        if (hasState && hasZip) {
          // Complete address with state and zip
          this.addressSelected = true;
          this.handleAddressChange(result, runDataPulls);
        } else {
          // Incomplete address - missing state or zip in components
          this.hasError = true;
          this.formControl.setErrors({ incompleteAddress: true });
          this.errorMessage = 'Please enter a complete address with state and zip code';
          this.cdr.detectChanges();
        }
      } else {
        // Invalid address or geocoding failed
        this.hasError = true;
        this.formControl.setErrors({ addressSelection: true });
        this.errorMessage = 'You must select an address from the dropdown';
        this.cdr.detectChanges();
      }
    });
  }

  checkForValidToken() {
    if (!this.token) {
      this.formViewService.updateForm.next(true);
    }
  }

  handleAddressChange(address: google.maps.GeocoderResult, runDataPulls = true) {
    this.addressSelected = true;
    this.formViewService.addressSubject.next({
      ...this.formViewService.addressSubject.getValue(),
      googleEvent: {
        address_components: address.address_components,
        formatted_address: address.formatted_address
      }
    });
    this.sendPosthogEvent('Google Address Autocomplete Clicked');
    try {
      const addressFieldKey = this.field.templateOptions.dataKey;

      const indexArray = address['address_components'].map(item => {
        return item.types[0];
      });

      const streetNumber = address['address_components'][indexArray.indexOf('street_number')] ? address['address_components'][indexArray.indexOf('street_number')].long_name : null;

      const streetName = address['address_components'][indexArray.indexOf('route')] ? address['address_components'][indexArray.indexOf('route')].long_name : null;

      this.streetAddress = streetNumber && streetName ? `${streetNumber} ${streetName}` : null;

      // Need to account for neighborhoods like Jamaica, Queens
      let cityCode = indexArray.indexOf('locality');
      if (cityCode < 0) {
        const fallbackTypes = ['sublocality_level_1', 'sublocality_level_2', 'neighborhood', 'political'];

        for (const type of fallbackTypes) {
          const index = indexArray.indexOf(type);
          if (index >= 0) {
            cityCode = index;
            break;
          }
        }
      }
      this.city = address['address_components'][cityCode] ? address['address_components'][cityCode].long_name : null;

      const county = address['address_components'][indexArray.indexOf('administrative_area_level_2')]
        ? address['address_components'][indexArray.indexOf('administrative_area_level_2')].long_name
        : null;

      this.state = address['address_components'][indexArray.indexOf('administrative_area_level_1')]
        ? address['address_components'][indexArray.indexOf('administrative_area_level_1')].short_name
        : null;

      this.zipCode = address['address_components'][indexArray.indexOf('postal_code')] ? address['address_components'][indexArray.indexOf('postal_code')].long_name : null;

      const fullAddress = address['formatted_address'];

      if (fullAddress) this.formControl.patchValue(fullAddress);

      const streetNumberField = this.field.parent.fieldGroup.find(field => {
        return field.templateOptions.dataKey === addressFieldKey + 'streetNumber';
      });

      if (streetNumberField) {
        streetNumberField.formControl.patchValue(streetNumber);
      }

      const streetNameField = this.field.parent.fieldGroup.find(field => {
        return field.templateOptions.dataKey === addressFieldKey + 'streetName';
      });

      if (streetNameField) {
        streetNameField.formControl.patchValue(streetName);
      }

      const streetAddressField = this.field.parent.fieldGroup.find(field => {
        return field.templateOptions.dataKey === addressFieldKey + 'streetAddress';
      });

      if (streetAddressField) {
        streetAddressField.formControl.patchValue(this.streetAddress);
      }

      const cityField = this.field.parent.fieldGroup.find(field => {
        return field.templateOptions.dataKey === addressFieldKey + 'city';
      });

      if (cityField) {
        cityField.formControl.patchValue(this.city);
      }

      const stateField = this.field.parent.fieldGroup.find(field => {
        return field.templateOptions.dataKey === addressFieldKey + 'state';
      });

      if (stateField) {
        stateField.formControl.patchValue(this.state);
      }

      const zipCodeField = this.field.parent.fieldGroup.find(field => {
        return field.templateOptions.dataKey === addressFieldKey + 'zipCode';
      });

      if (zipCodeField) {
        zipCodeField.formControl.patchValue(this.zipCode);
        this.submissionService.onUpdateMetadata(
          {
            key: 'zipCode',
            value: this.zipCode
          },
          this.field
        );
      }

      const countyField = this.field.parent.fieldGroup.find(field => {
        return field.templateOptions.dataKey === addressFieldKey + 'county';
      });

      if (countyField) {
        countyField.formControl.patchValue(county);
      }

      this.submissionService.onUpdateMetadata(
        {
          key: 'fullAddress',
          value: fullAddress
        },
        this.field
      );
      if (streetNumber && streetName && this.city && this.state && this.zipCode) {
        if (this.to.className !== 'xilo-input') {
          this.formViewService.updateForm.next(true);
        }
        if (this.field?.parent?.templateOptions?.hasRepeatButtons && runDataPulls) {
          const key = this.field?.parent?.key;
          if (typeof key === 'string') {
            const sectionIndex = parseInt(key, 10);
            if (!isNaN(sectionIndex)) {
              this.integrationMappingService.initDataPull(this.to?.pullDataTrigger, this.to?.dataPullMappingId, sectionIndex);
            }
          } else throw new Error(`Invalid sectionIndex: ${key} cannot be parsed as a number.`);
        } else if (runDataPulls) {
          this.integrationMappingService.initDataPull(this.to?.pullDataTrigger, this.to?.dataPullMappingId, this.field?.parent?.parent?.templateOptions?.subIndex);
        }

        this.hasError = false;
        this.errorMessage = null;
        this.sendPosthogEvent('Google Address Autocomplete Completed');
        setTimeout(() => {
          this.addressSelected = true;
          this.formControl.setErrors(null);
          this.formControl.markAllAsTouched();
          this.formControl.updateValueAndValidity();
        });

        setTimeout(() => {
          this.addressSelected = false;
        }, 1000);
      } else {
        this.hasError = true;
        this.addressSelected = false;

        // Create a message that tells the user which fields are missing
        const missingFields = [];
        if (!streetNumber || !streetName) missingFields.push('street address');
        if (!this.city) missingFields.push('city');
        if (!this.state) missingFields.push('state');
        if (!this.zipCode) missingFields.push('zip code');

        const missingFieldsText = missingFields.join(', ');
        this.errorMessage = `Invalid address was selected. Missing: ${missingFieldsText}. Please select a complete address.`;
        this.sendPosthogEvent('Google Address Autocomplete Invalid Address');
      }
    } catch (error) {
      this.sendPosthogEvent('Google Address Autocomplete Failed');
      console.log('Google Address Autocomplete Error: %o', error);
      this.addressSelected = false;
    }
  }

  sendPosthogEvent(eventName: string) {
    const tokenDetails = decodeToken(this.token) || {};
    const companyId = get(tokenDetails, 'client.companyClientId');
    if (companyId) {
      this.posthogService.captureDashboardEvent(eventName, {
        source: 'Intake Form',
        company_id: companyId,
        email: null,
        event_type: 'Adoption'
      });
    }
  }

  openZillow(address) {
    const fullAddress = address.replace(/(?: USA)/g, '');
    this.formViewService
      .listZillowData({ fullAddress })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        data => {
          window.open(data, '_blank');
        },
        error => {
          this.toastService.showWarn(error.error.error.message);
          console.log(error, false);
        }
      );
  }

  private setPlacesOptions() {
    // Note: [options] for ngx-google-places-autocomplete conflicts with formly, so this must be done manually
    this.placesRef.options = this.googlePlacesOptions;
    this.placesRef.reset();
  }

  // attachLastAddressOption : listen to the first address comp. instance. When field is completed
  // all other addresses should be flagged with showLastAddress = true to allow copying.
  attachLastAddressOption() {
    this.field.formControl?.valueChanges
      .pipe(
        debounceTime(500),
        filter(value => this.field.formControl.status === 'VALID' && value !== ''),
        takeUntil(this.destroyed$)
      )
      .subscribe(value => {
        this.formViewService.addressSubject.next({
          ...this.formViewService.addressSubject.getValue(),
          dataKey: this.field.templateOptions?.dataKey,
          value
        });
      });
    this.formViewService.addressSubject.pipe(takeUntil(this.destroyed$)).subscribe(cachedAddress => {
      this.field.parent.fieldGroup.forEach(fg => {
        if (fg.type === 'address' && fg.templateOptions.dataKey !== cachedAddress.dataKey && cachedAddress.value !== '') {
          fg.templateOptions.showLastAddress = true;
        } else {
          fg.templateOptions.showLastAddress = false;
        }
        this.cdr.markForCheck();
      });
    });
  }

  copyAddress() {
    const address: any = this.formViewService.addressSubject.getValue().googleEvent;
    this.handleAddressChange(address, true);
  }

  toggleParsedAddressFields(showFields) {
    const addressFieldKey = this.field.templateOptions?.dataKey;
    this.field.parent.fieldGroup.forEach(element => {
      const fieldDataKey = element?.templateOptions?.dataKey;
      const addressOpts = ['zipCode', 'state', 'county', 'city', 'streetNumber', 'streetName', 'streetAddress'];
      for (const aO of addressOpts) {
        if (fieldDataKey === addressFieldKey + aO) {
          const isAddressField = fieldDataKey.includes(aO);
          if (isAddressField && element.type !== 'address') {
            element.hide = showFields;
          }
        }
      }
      if (element.type === 'address' && fieldDataKey === addressFieldKey) {
        this.addressFieldText = showFields ? 'Show address details' : 'Hide address details';
        this.addressIsShowing = !showFields;
      }
    });
  }

  openResearchPanel() {
    const address = this.formViewService.addressSubject.getValue()?.value || this.formControl.value;
    if (address) {
      this.formViewService.researchPanelState.next({
        openPanel: true,
        address: address
      });
    }
  }

  // Unified parser for address components from Google APIs
  // Accepts components from both Geocoder and Places Autocomplete
  private parseAddressComponents(components: google.maps.GeocoderAddressComponent[] | Address['address_components']): any {
    if (!components) return {};

    // Type guard or duck typing might be needed if structures differ significantly,
    // but often they are compatible enough.
    const getComponent = (type: string, useShortName = false): string | null => {
      const component = components.find(comp => comp.types.includes(type));
      // Ensure component has the expected properties before accessing
      if (component && typeof component.long_name === 'string' && typeof component.short_name === 'string') {
        return useShortName ? component.short_name : component.long_name;
      }
      return null;
    };

    const streetNumber = getComponent('street_number');
    const streetName = getComponent('route');
    let city = getComponent('locality');
    if (!city) {
      city = getComponent('sublocality_level_1'); // Handle cases like NYC boroughs
    }
    const county = getComponent('administrative_area_level_2');
    const state = getComponent('administrative_area_level_1', true); // Use short name (e.g., CA)
    const zipCode = getComponent('postal_code');
    const streetAddress = streetNumber && streetName ? `${streetNumber} ${streetName}` : null;

    return { streetAddress, streetNumber, streetName, city, state, zipCode, county };
  }

  // Unified method to update related Formly fields
  private updateRelatedFields(parsedData: any): void {
    const addressFieldKey = this.field.templateOptions.dataKey;

    const findAndPatch = (suffix: string, value: string | null) => {
      const patchValue = value ?? ''; // Use empty string for null/undefined
      const field = this.field.parent.fieldGroup.find(f => f.templateOptions.dataKey === addressFieldKey + suffix);
      if (field) {
        // Only patch if value differs and there's no existing value
        // This prevents overwriting user-entered data
        if (field.formControl.value !== patchValue && !field.formControl.value) {
          field.formControl.patchValue(patchValue);
          field.formControl.markAsDirty();
        }
      }
    };

    findAndPatch('streetNumber', parsedData.streetNumber);
    findAndPatch('streetName', parsedData.streetName);
    findAndPatch('streetAddress', parsedData.streetAddress);
    findAndPatch('city', parsedData.city);
    findAndPatch('state', parsedData.state);
    findAndPatch('zipCode', parsedData.zipCode);
    findAndPatch('county', parsedData.county);
  }

  private geocodeAddressString(address: string): void {
    // Check if Google Maps API and Geocoder are loaded
    if (typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
      console.warn('Google Maps Geocoder API not available.');
      // Optionally provide feedback to the user
      // this.toastService.showError('Address lookup service is unavailable.');
      return;
    }

    // Instantiate the standard Google Maps Geocoder
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ address: address, componentRestrictions: { country: 'US' } }, (results, status) => {
      // Use the standard GeocoderStatus enum
      if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
        const geocodedResult = results[0];
        console.log('Geocoded Result:', geocodedResult);
        // Pass the address_components from the standard Geocoder result
        const parsedData = this.parseAddressComponents(geocodedResult.address_components);

        // Check if we got a reasonably complete address
        if (parsedData.streetAddress && parsedData.city && parsedData.state) {

          // Update related fields using the helper
          this.updateRelatedFields(parsedData);

        } else {
          // Geocoding succeeded but didn't yield a complete address
          console.warn('Geocoding did not return a complete address from string:', geocodedResult);
        }
      // Use the standard GeocoderStatus enum
      } else if (status !== google.maps.GeocoderStatus.ZERO_RESULTS) {
        // Handle geocoding errors (excluding ZERO_RESULTS which just means not found)
        console.error('Geocoding failed for string:', status);
      } else {
        // Log ZERO_RESULTS for debugging if needed
        console.log('Geocoding returned ZERO_RESULTS for string:', address);
        // No action needed here, the input remains as typed by the user
      }
    });
  }
}
